import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { DEFAULT_INDEX_GROUP } from '@/constants/constants';
import type { SymbolKey } from '@/entities/symbol/types';
import type { OtherGroupIndexEntry } from '@/features/Markets/api/otherGroupIndex.api';
import { fetchOtherGroupIndex } from '@/features/Markets/api/otherGroupIndex.api';
import type { CommoditiesTableRowType, CommoditiesTableSectionType } from '../types';

type DataRecord = Partial<Record<string, OtherGroupIndexEntry[]>>;
type RowType = CommoditiesTableSectionType['rows'][number];

function symbolKeyString(key: SymbolKey): string {
  return `${key.Code}-${key.PeriodType}-${key.Period}-${key.TimeRef}`;
}

function getDecimalPlaces(value: string): number {
  const dot = value.indexOf('.');
  return dot >= 0 ? value.length - dot - 1 : 0;
}

function priceValue(values: OtherGroupIndexEntry['values'] | undefined): string | undefined {
  return values?.Index ?? values?.Mid;
}

function calculateDelta(current: string, previous: string): string | null {
  if (!current || !previous) return null;
  const scale = getDecimalPlaces(current);
  return (Number.parseFloat(current) - Number.parseFloat(previous)).toFixed(scale);
}

function enrichFromData(
  data: OtherGroupIndexEntry[],
  role: 'symbol' | 'diffSymbol'
): Partial<CommoditiesTableRowType> {
  if (data.length === 0) return {};
  const first = data[0];
  const firstPrice = priceValue(first.values);
  const secondPrice = priceValue(data[1]?.values);

  if (role === 'symbol') {
    const patch: Partial<CommoditiesTableRowType> = {
      date: first.transactionTime,
      last: firstPrice,
      periodAbs: first.values.PeriodAbs,
      metadata: first.metadata
    };
    if (firstPrice && secondPrice) {
      patch.delta = calculateDelta(firstPrice, secondPrice) ?? undefined;
    }
    return patch;
  }

  return {
    diff: firstPrice,
    diffPeriodAbs: first.values.PeriodAbs,
    diffDelta: firstPrice && secondPrice ? calculateDelta(firstPrice, secondPrice) : null,
    diffMetadata: first.metadata
  };
}

type SymbolToFetch = { key: SymbolKey; group: string };

function addIfNew(
  sym: { key: SymbolKey; group: string },
  seen: Set<string>,
  symbols: SymbolToFetch[]
): void {
  if (sym.group !== DEFAULT_INDEX_GROUP && sym.key.Code) {
    const k = symbolKeyString(sym.key);
    if (!seen.has(k)) {
      seen.add(k);
      symbols.push({ key: sym.key, group: sym.group });
    }
  }
}

export function collectOtherGroupSymbols(
  sections: CommoditiesTableSectionType[] | undefined
): SymbolToFetch[] {
  if (!sections) return [];
  return collectSymbolsToFetch(sections);
}

function collectSymbolsToFetch(sections: CommoditiesTableSectionType[]): SymbolToFetch[] {
  const symbols: SymbolToFetch[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    for (const row of section.rows) {
      addIfNew(row.symbol, seen, symbols);
      addIfNew(row.diffSymbol, seen, symbols);
    }
  }
  return symbols;
}

function enrichRow(row: RowType, dataRecord: DataRecord): RowType {
  let enriched = row;
  if (row.symbol.group !== DEFAULT_INDEX_GROUP && row.symbol.key.Code) {
    const data = dataRecord[symbolKeyString(row.symbol.key)];
    if (data) enriched = { ...enriched, ...enrichFromData(data, 'symbol') };
  }
  if (row.diffSymbol.group !== DEFAULT_INDEX_GROUP && row.diffSymbol.key.Code) {
    const data = dataRecord[symbolKeyString(row.diffSymbol.key)];
    if (data) enriched = { ...enriched, ...enrichFromData(data, 'diffSymbol') };
  }
  return enriched;
}

export function hasOtherGroupSymbols(sections: CommoditiesTableSectionType[] | undefined): boolean {
  if (!sections || sections.length === 0) return false;
  return sections
    .flatMap(s => s.rows.flatMap(r => [r.symbol, r.diffSymbol]))
    .some(sym => sym?.key?.Code && sym.group !== DEFAULT_INDEX_GROUP);
}

export function useOtherGroupData(sections: CommoditiesTableSectionType[] | undefined): {
  sections: CommoditiesTableSectionType[] | undefined;
  isLoading: boolean;
} {
  const symbolsToFetch = useMemo(
    () => (sections ? collectSymbolsToFetch(sections) : []),
    [sections]
  );

  const { dataRecord, isLoading } = useQueries({
    queries: symbolsToFetch.map(sym => ({
      queryKey: ['otherGroupIndex', sym.group, symbolKeyString(sym.key)],
      queryFn: () => fetchOtherGroupIndex(sym.key, sym.group),
      staleTime: 300_000
    })),
    combine: queryResults => {
      const record: DataRecord = {};
      symbolsToFetch.forEach((sym, i) => {
        const data = queryResults[i]?.data;
        if (data) record[symbolKeyString(sym.key)] = data;
      });
      return {
        dataRecord: record,
        isLoading: queryResults.some(r => r.isLoading)
      };
    }
  });

  const enrichedSections = useMemo(() => {
    if (!sections) return undefined;
    if (symbolsToFetch.length === 0) return sections;
    return sections.map(section => ({
      ...section,
      rows: section.rows.map(row => enrichRow(row, dataRecord))
    }));
  }, [sections, symbolsToFetch, dataRecord]);

  return { sections: enrichedSections, isLoading };
}
